import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, fade } from '@material-ui/core/styles';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@material-ui/core';
import { Archive as ArchiveIcon } from '@material-ui/icons';
import stiliDialog from '../../../../components/stiliDialog';

export const ARCHIVIA_SPIEGAZIONE =
  "Esce dall'elenco degli Allegati e resta consultabile in Archivio, da dove puoi ripristinarlo quando vuoi. Le esportazioni continuano a includerlo, segnato come archiviato.";

// The title stays taken in the folder while the document is archived, so the
// usual next step — uploading the new version under the same name — would be
// refused. Said before the operator archives, not after the refusal.
export const ARCHIVIA_TITOLO_OCCUPATO =
  'Il titolo resta occupato nella cartella: per caricare una nuova versione usa un titolo diverso.';

// The layout of the section's delete confirmation, in the brand tint instead of
// the red: archiving loses nothing and can be undone from Archivio.
const useStyles = makeStyles((theme) => {
  const dialog = stiliDialog(theme);
  return {
    ...dialog,
    paper: {
      ...dialog.paper,
      overflow: 'hidden',
    },
    content: {
      padding: theme.spacing(4, 3, 1),
      textAlign: 'center',
    },
    icona: {
      width: 56,
      height: 56,
      borderRadius: theme.shape.iconTileRadius,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: fade(theme.palette.primary.main, 0.12),
      color: theme.palette.primary.dark,
      margin: '0 auto',
      marginBottom: theme.spacing(3),
    },
    titolo: {
      ...dialog.titolo,
      marginBottom: theme.spacing(1),
    },
    nota: {
      ...dialog.testo,
      marginTop: theme.spacing(1.5),
    },
    actions: {
      justifyContent: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(1.5, 3, 3),
    },
    annulla: {
      ...dialog.secondario,
      color: theme.palette.text.secondary,
    },
  };
});

/** The confirmation before a document leaves the Allegati view for Archivio. */
export default function ArchiviaDocumentoDialog({
  open,
  titolo,
  saving,
  onCancel,
  onConfirm,
}) {
  const classes = useStyles();

  // The caller empties the item in the same commit that closes the window, so
  // the title would vanish while MUI is still fading it out.
  const ultimoTitolo = useRef(titolo);
  if (open) ultimoTitolo.current = titolo;
  const titoloMostrato = open ? titolo : ultimoTitolo.current;

  return (
    <Dialog
      open={open}
      // Not while the write is in flight: closing the window would not stop it.
      onClose={saving ? undefined : onCancel}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: classes.paper }}
      aria-labelledby="archivia-documento-titolo"
    >
      <DialogContent className={classes.content}>
        <div className={classes.icona}>
          <ArchiveIcon />
        </div>
        <Typography id="archivia-documento-titolo" className={classes.titolo}>
          {`Archiviare "${titoloMostrato}"?`}
        </Typography>
        <Typography variant="body2" className={classes.testo}>
          {ARCHIVIA_SPIEGAZIONE}
        </Typography>
        <Typography variant="body2" className={classes.nota}>
          {ARCHIVIA_TITOLO_OCCUPATO}
        </Typography>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onCancel}
          disabled={saving}
          className={classes.annulla}
        >
          Annulla
        </Button>
        <Button
          onClick={onConfirm}
          disabled={saving}
          color="primary"
          variant="contained"
          className={classes.primario}
          // The spinner replaces the label while the write is in flight; the
          // name stays so the button does not go silent at that moment.
          aria-label="Archivia"
          aria-busy={!!saving}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Archivia'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ArchiviaDocumentoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  titolo: PropTypes.string,
  saving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ArchiviaDocumentoDialog.defaultProps = {
  titolo: '',
  saving: false,
};
